import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { auth, db } from "../../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import {
  getImpactNetwork,
  getImpactNetworkMembers,
} from "../../services/impactNetworks";

import "./ImpactNetworks.css";
import ImpactMap from "../../components/ImpactMap";


function formatMemberName(member) {
  if (member.displayName) return member.displayName;

  if (member.firstName) {
    const lastInitial = member.lastName?.charAt(0) || "";
    return `${member.firstName}${lastInitial ? ` ${lastInitial}.` : ""}`;
  }

  return "Member";
}

function formatCurrency(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function formatImpactLabel(unit) {
  return String(unit || "impact")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getTime(value) {
  if (!value) return 0;
  if (value?.toDate) return value.toDate().getTime();
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatActivityDate(value) {
  const time = getTime(value);
  if (!time) return "";
  return new Date(time).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export default function ImpactNetworkDashboard() {
  const { networkId } = useParams();
  const user = auth.currentUser;

  const [network, setNetwork] = useState(null);
  const [members, setMembers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadDashboard() {
      try {
        setLoading(true);

        const networkData = await getImpactNetwork(networkId);
        setNetwork(networkData);

        if (!networkData) return;

        try {
          setMembers(await getImpactNetworkMembers(networkId));
        } catch (err) {
          console.error("Failed to load members:", err);
          setMembers([]);
        }

        try {
          const subsSnap = await getDocs(
            query(collection(db, "subscriptions"), where("networkId", "==", networkId))
          );

          setSubscriptions(
            subsSnap.docs.map((d) => ({
              id: d.id,
              ...d.data(),
            }))
          );
        } catch (err) {
          console.error("Failed to load attributed subscriptions:", err);
          setSubscriptions([]);
        }

        try {
          const ordersSnap = await getDocs(
            query(collection(db, "orders"), where("networkId", "==", networkId))
          );

          setOrders(
            ordersSnap.docs.map((d) => ({
              id: d.id,
              ...d.data(),
            }))
          );
        } catch (err) {
          console.error("Failed to load network orders:", err);
          setOrders([]);
        }
      } catch (err) {
        console.error("Failed to load Impact Network dashboard:", err);
      } finally {
        setLoading(false);
      }
    }

    loadDashboard();
  }, [networkId]);

  const activeSubscriptions = useMemo(
    () => subscriptions.filter((s) => s.status === "active"),
    [subscriptions]
  );

  const networkStats = useMemo(() => {
    const orgCount = new Set(
      activeSubscriptions
        .map((s) => s.orgId || s.organizationId || s.orgName)
        .filter(Boolean)
    ).size;

    const monthlySupport = activeSubscriptions.reduce(
      (sum, s) => sum + Number(s.pricePerCycle || 0),
      0
    );

    const byUnit = {};
    activeSubscriptions.forEach((s) => {
      const unit = (s.impactUnit || "impact").trim();
      const amount =
        Number(s.totalImpact || 0) ||
        Number(s.impactQuantity || 0) ||
        Number(s.quantityPerCycle || 0) ||
        0;

      if (!amount) return;
      byUnit[unit] = (byUnit[unit] || 0) + amount;
    });

    const topImpacts = Object.entries(byUnit)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([unit, amount]) => ({ unit, amount }));

    return {
      members: members.length,
      activeCommitments: activeSubscriptions.length,
      monthlySupport,
      orgCount,
      topImpacts,
    };
  }, [activeSubscriptions, members.length]);

  const recentActivity = useMemo(() => {
    const subscriptionById = {};
    subscriptions.forEach((sub) => {
      subscriptionById[sub.id] = sub;
    });

    const commitmentActivity = subscriptions.map((sub) => ({
      id: `sub-${sub.id}`,
      type: "commitment",
      title: "New commitment added",
      description: `${sub.donorFirstName || "A member"} adopted ${
        sub.itemName || "a need"
      } for ${sub.orgName || "a nonprofit partner"}.`,
      date: sub.createdAt,
      sortTime: getTime(sub.createdAt),
    }));

    const memberActivity = members.map((member) => ({
      id: `member-${member.uid}`,
      type: "member",
      title: "New member joined",
      description: `${formatMemberName(member)} joined ${
        network?.name || "this network"
      }.`,
      date: member.joinedAt,
      sortTime: getTime(member.joinedAt),
    }));

    const orderActivity = orders.flatMap((order) => {
      const sub = subscriptionById[order.subscriptionId] || {};

      const itemName =
        order.itemName ||
        order.needName ||
        order.productName ||
        sub.itemName ||
        "A supported need";

      const orgName =
        order.orgName ||
        order.organizationName ||
        sub.orgName ||
        "a nonprofit partner";

      const orderedAt = order.orderedAt || order.createdAt;
      const shippedAt = order.shippedAt || order.shippedDate || order.dateShipped;
      const deliveredAt =
        order.deliveredAt || order.deliveredDate || order.dateDelivered;

      const events = [];

      if (orderedAt) {
        events.push({
          id: `order-${order.id}-ordered`,
          type: "order_ordered",
          title: "Order placed",
          description: `${itemName} for ${orgName} has been ordered.`,
          date: orderedAt,
          sortTime: getTime(orderedAt),
        });
      }

      if (shippedAt) {
        events.push({
          id: `order-${order.id}-shipped`,
          type: "order_shipped",
          title: "Order shipped",
          description: `${itemName} for ${orgName} has shipped.`,
          date: shippedAt,
          sortTime: getTime(shippedAt),
        });
      }

      if (deliveredAt) {
        events.push({
          id: `order-${order.id}-delivered`,
          type: "order_delivered",
          title: "Order delivered",
          description: `${itemName} was delivered to ${orgName}.`,
          date: deliveredAt,
          sortTime: getTime(deliveredAt),
        });
      }

      return events;
    });

    return [...commitmentActivity, ...memberActivity, ...orderActivity]
      .filter((item) => item.sortTime)
      .sort((a, b) => b.sortTime - a.sortTime)
      .slice(0, 20);
  }, [subscriptions, members, orders, network?.name]);

  if (!user) {
    return (
      <div className="impact-network-page">
        Please log in to view this Impact Network.
      </div>
    );
  }

  if (loading) {
    return <div className="impact-network-page">Loading Impact Network...</div>;
  }

  if (!network) {
    return (
      <div className="impact-network-page">
        <h1>Impact Network Not Found</h1>
        <p>This network may not exist or you may not have access.</p>
      </div>
    );
  }

  const inviteUrl = `${window.location.origin}/join-network/${network.inviteCode}`;

  return (
    <div className="impact-network-page">
      <nav className="network-top-nav">
        <Link to="/" className="network-brand-link">
          somebody.gives
        </Link>

        <div className="network-top-nav-links">
          <Link to="/give/browse">Browse Needs</Link>
          <Link to="/donor/impact-networks">My Networks</Link>
          <Link to="/give/dashboard">My Dashboard</Link>
        </div>
      </nav>

      <section className="network-hero">
        <div>
          <div className="network-eyebrow">Impact Network</div>
          <h1>{network.name}</h1>
          <p className="network-hero-text">
            {network.description ||
              "Collective impact through recurring nonprofit needs."}
          </p>
          <p className="network-hero-subtext">
            {networkStats.members} member
            {networkStats.members === 1 ? "" : "s"} have attributed{" "}
            {networkStats.activeCommitments} active commitment
            {networkStats.activeCommitments === 1 ? "" : "s"} to this network.
          </p>
        </div>

        <Link className="network-secondary-button" to="/donor/impact-networks">
          My Networks
        </Link>
      </section>

<section className="network-panel">
  <ImpactMap
    activeSubscriptions={activeSubscriptions}
    centerType="network"
    centerLabel={network.name}
    centerSubtext={`${networkStats.members} members • ${networkStats.activeCommitments} commitments • ${formatCurrency(networkStats.monthlySupport)}/month`}
  />
</section>

      <section className="network-stats-grid">
        <div className="network-stat-card">
          <span>Members</span>
          <strong>{networkStats.members}</strong>
        </div>
        <div className="network-stat-card">
          <span>Active Commitments</span>
          <strong>{networkStats.activeCommitments}</strong>
        </div>
        <div className="network-stat-card">
          <span>Monthly Support</span>
          <strong>{formatCurrency(networkStats.monthlySupport)}</strong>
        </div>
        <div className="network-stat-card">
          <span>Organizations Supported</span>
          <strong>{networkStats.orgCount}</strong>
        </div>
      </section>

      <section className="network-panel network-impact-panel">
        <div>
          <div className="network-eyebrow">Collective Impact</div>
          <h2>{network.name} Impact</h2>

          {networkStats.activeCommitments === 0 ? (
            <p>No active commitments have been attributed to this network yet.</p>
          ) : (
            <p>
              This network is providing{" "}
              <strong>{formatCurrency(networkStats.monthlySupport)}/month</strong>{" "}
              in recurring support across{" "}
              <strong>{networkStats.orgCount}</strong> organization
              {networkStats.orgCount === 1 ? "" : "s"}.
            </p>
          )}
        </div>

        {networkStats.topImpacts.length > 0 && (
          <div className="network-impact-proof-grid">
            {networkStats.topImpacts.map((item) => (
              <div key={item.unit} className="network-impact-proof-card">
                <strong>{item.amount.toLocaleString()}</strong>
                <span>{formatImpactLabel(item.unit)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="network-panel">
        <div className="network-eyebrow">Attributed Giving</div>
        <h2>Network Commitments</h2>

        {activeSubscriptions.length === 0 ? (
          <p>No active attributed commitments yet.</p>
        ) : (
          <div className="network-commitment-grid">
            {activeSubscriptions.map((sub) => (
              <article className="network-commitment-card" key={sub.id}>
                <div className="network-commitment-org">{sub.orgName}</div>
                <h3>{sub.itemName}</h3>
                
                {sub.impactStatement && <p>{sub.impactStatement}</p>}
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="network-two-column">
        <div className="network-panel">
          <div className="network-eyebrow">Recruit</div>
          <h2>Invite Members</h2>
          <p>Share this link with group members.</p>

          <div className="network-copy-box">
            <input value={inviteUrl} readOnly />
            <button type="button" onClick={() => navigator.clipboard.writeText(inviteUrl)}>
              Copy
            </button>
          </div>
        </div>

        <div className="network-panel">
          <div className="network-eyebrow">Focus</div>
          <h2>Focus Areas</h2>

          {network.focusAreas?.length > 0 ? (
            <div className="network-tag-row">
              {network.focusAreas.map((area) => (
                <span className="network-tag" key={area}>
                  {area}
                </span>
              ))}
            </div>
          ) : (
            <p>No preferred focus areas selected.</p>
          )}
        </div>
      </section>

      <section className="network-two-column">
        <div className="network-panel">
          <div className="network-eyebrow">Updates</div>
          <h2>Recent Activity</h2>

          {recentActivity.length === 0 ? (
            <p>
              No recent activity yet. New members, attributed commitments, and order
              updates will appear here.
            </p>
          ) : (
            <div className="network-simple-list">
              {recentActivity.map((item) => (
                <div key={item.id}>
                  <strong>{item.title}</strong>
                  <p>{item.description}</p>
                  <p>{formatActivityDate(item.date)}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="network-panel">
          <div className="network-eyebrow">Community</div>
          <h2>Members</h2>

          {members.length === 0 ? (
            <p>No members yet.</p>
          ) : (
            <div className="network-simple-list">
              {members.map((member) => (
                <div key={member.uid}>
                  <strong>{formatMemberName(member)}</strong>
                  <p>{member.role}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}